'use client';

import { createRoute } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';

import MarketingLayout from '@/components/layouts/MarketingLayout';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  head: () => seoHead('/privacy'),
  component: Privacy,
});

const sections = [
  {
    heading: 'Overview',
    body: 'This policy explains what information Mindrealm collects, how it is used, and the choices you have. We aim to collect only what we need to run the service.',
  },
  {
    heading: 'Information we collect',
    body: 'Account information (such as your name and email) through our authentication provider; billing information through our payment provider; the source code of repositories you connect, in order to review it; and usage and diagnostic data needed to operate and improve the service.',
  },
  {
    heading: 'Your code',
    body: 'When you connect a repository, Mindrealm clones it to produce your findings. We use your code only to provide the review service. We do not use it to train models, and we do not sell it or share it except with the providers described below that are required to run the service.',
  },
  {
    heading: 'How we use information',
    body: 'To provide and maintain the service, generate review findings, process payments, provide support, secure the service, and meet legal obligations.',
  },
  {
    heading: 'Service providers',
    body: 'We rely on a small set of providers to operate Mindrealm: authentication, payment processing, source-control integration, database and application hosting, and observability. These providers process data only as needed to deliver their part of the service.',
  },
  {
    heading: 'Data retention',
    body: 'We keep account and billing records for as long as your account is active and as required to meet legal and accounting obligations. Cloned source is used to produce findings and is not retained as a long-term archive of your code.',
  },
  {
    heading: 'Security',
    body: 'We use industry-standard measures to protect data in transit and at rest. No system is perfectly secure, but we work to limit access to data to what is necessary to run the service.',
  },
  {
    heading: 'Your rights',
    body: 'You may request access to or deletion of your personal information, subject to legal limits. Contact us to make a request.',
  },
  {
    heading: 'Changes',
    body: 'We may update this policy from time to time. We will update the date above when we do.',
  },
  {
    heading: 'Contact',
    body: 'Privacy questions can be sent to privacy@mindrealm.ai.',
  },
];

function PrivacyContent() {
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const showLoading = !mounted;

  if (showLoading) {
    return (
      <MarketingLayout isLoading={true} isSignedIn={false}>
        <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
          <div className="mx-auto max-w-[760px]">
            <Skeleton className="mb-8 h-12 w-[40%]" />
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout isLoading={false} isSignedIn={false}>
      <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
        <div
          className="mx-auto max-w-[760px]"
          style={{ fontFamily: theme.fonts.body }}
        >
          <h1
            className="text-gradient-hero mb-2 text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: theme.fonts.display, lineHeight: 1.2 }}
          >
            Privacy Policy
          </h1>
          <p className="text-foreground/60 mb-6 text-sm">
            Last updated: June 2, 2026.
          </p>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.heading}>
                <h2
                  className="mb-2 text-xl font-semibold"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  {section.heading}
                </h2>
                <p className="text-foreground/80">{section.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Privacy() {
  return (
    <Suspense
      fallback={
        <MarketingLayout isLoading={true} isSignedIn={false}>
          <section className="px-6 pt-24 pb-16 md:px-16 md:pt-32">
            <div className="mx-auto max-w-[760px]">
              <Skeleton className="mb-8 h-12 w-[40%]" />
              <Skeleton className="h-96 w-full rounded-xl" />
            </div>
          </section>
        </MarketingLayout>
      }
    >
      <PrivacyContent />
    </Suspense>
  );
}
